import { Send } from 'lucide-react';

interface Props {
  mode: 'COMPOSER' | 'MAILTO' | 'GMAIL';
  connected: boolean;
  onChange: (mode: 'COMPOSER' | 'MAILTO' | 'GMAIL') => void;
}

const RADIO_CLASS =
  'h-4 w-4 accent-blast-600 dark:accent-blast-400 cursor-pointer disabled:cursor-not-allowed';

/**
 * Settings control: choose what clicking a contact's email does — compose
 * in the CRM (instant, always-on logging), open in Gmail (app or web), or
 * open the rep's own mail app. The two handoff options (Gmail, mail app)
 * are gated until Gmail is connected, since that's what logs the send.
 */
export function EmailComposeModeControl({ mode, connected, onChange }: Props) {
  return (
    <fieldset className="space-y-2">
      <legend className="label flex items-center gap-2">
        <Send className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        When you click a contact's email
      </legend>
      <label className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
        <input
          type="radio"
          name="email-compose-mode"
          checked={mode === 'COMPOSER'}
          onChange={() => onChange('COMPOSER')}
          className={RADIO_CLASS}
        />
        Compose in the CRM
      </label>
      <label
        className={`flex items-center gap-2 py-1 text-sm ${
          connected
            ? 'text-gray-700 dark:text-gray-300 cursor-pointer'
            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
      >
        <input
          type="radio"
          name="email-compose-mode"
          checked={mode === 'GMAIL'}
          disabled={!connected}
          onChange={() => onChange('GMAIL')}
          className={RADIO_CLASS}
        />
        Open in Gmail (app or web)
      </label>
      <label
        className={`flex items-center gap-2 py-1 text-sm ${
          connected
            ? 'text-gray-700 dark:text-gray-300 cursor-pointer'
            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
      >
        <input
          type="radio"
          name="email-compose-mode"
          checked={mode === 'MAILTO'}
          disabled={!connected}
          onChange={() => onChange('MAILTO')}
          className={RADIO_CLASS}
        />
        Open my mail app
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        In-app compose logs instantly and always sends from your work address. Gmail and your own
        mail app are logged via Gmail sync (within ~5 min) — which is why they need Gmail connected —
        and send from whichever account that app is using.
      </p>
      {!connected && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Connect Gmail first — keeps your sent emails logged.
        </p>
      )}
    </fieldset>
  );
}
